"use client";

import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";

interface YearlyComparisonItem {
  tahun: string;
  total_pendaftar: number;
  total_validasi: number;
  total_lulus: number;
  total_daftar_kembali: number;
  total_isi_ukt: number;
  total_valid_isi_ukt: number;
  total_bayar_ukt: number;
}

interface ComparisonYearlyChartProps {
  data: YearlyComparisonItem[] | undefined;
  isLoading: boolean;
}

const chartConfig = {
  total_pendaftar: {
    label: "Pendaftar",
    color: "hsl(var(--chart-1))",
  },
  total_lulus: {
    label: "Lulus",
    color: "hsl(var(--chart-2))",
  },
  total_valid_isi_ukt: {
    label: "Valid Isi UKT",
    color: "hsl(var(--chart-4))",
  },
  total_daftar_kembali: {
    label: "Daftar Kembali",
    color: "hsl(var(--chart-5))",
  },
} satisfies ChartConfig;

export function ComparisonYearlyChart({
  data,
  isLoading,
}: ComparisonYearlyChartProps) {
  if (isLoading) {
    return (
      <Card className="flex flex-col h-full min-h-[400px]">
        <CardHeader>
          <Skeleton className="h-6 w-1/2 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-full w-full rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle>Perbandingan Tahunan</CardTitle>
        <CardDescription>
          Perbandingan statistik pendaftaran dari tahun ke tahun.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <ChartContainer config={chartConfig} className="min-h-[350px] w-full">
          <BarChart
            accessibilityLayer
            data={data}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="tahun"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              fontSize={12}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="dashed" />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              dataKey="total_pendaftar"
              fill="var(--color-total_pendaftar)"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
            <Bar
              dataKey="total_lulus"
              fill="var(--color-total_lulus)"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
            <Bar
              dataKey="total_valid_isi_ukt"
              fill="var(--color-total_valid_isi_ukt)"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
            <Bar
              dataKey="total_daftar_kembali"
              fill="var(--color-total_daftar_kembali)"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
